import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  GoldBotStatusProvider,
  useGoldBotStatusValue,
} from "../components/goldbot/GoldBotStatusProvider";
import { buildHeaderState } from "../components/goldbot/goldBotHeaderViewModel";
import GoldBotOverview from "./goldbot/GoldBotOverview";
import GoldBotPositions from "./goldbot/GoldBotPositions";
import GoldBotStrategies from "./goldbot/GoldBotStrategies";
import GoldBotLogs from "./goldbot/GoldBotLogs";
import GoldBotPerformance from "./goldbot/GoldBotPerformance";
import "./GoldBot.css";

const navigation = [
  { label: "Overview", id: "overview" },
  { label: "Open Positions", id: "positions" },
  { label: "Strategies", id: "strategies" },
  { label: "Logs", id: "logs" },
  { label: "Performance", id: "performance" },
];

function GoldBotLiveIndicator() {
  const { data, error, isLoading } = useGoldBotStatusValue();
  const header = buildHeaderState({ data, error, isLoading });

  return (
    <div className="goldbot-refresh" aria-label="Live data status">
      <div className={`goldbot-refresh__live is-${header.tone}`}>
        <span className={`goldbot-status-dot is-${header.tone}`} />{" "}
        {header.label}
      </div>
      <span>{header.detail}</span>
    </div>
  );
}

function GoldBotWorkspace() {
  const location = useLocation();

  useEffect(() => {
    const routeSection = location.pathname.split("/")[2];
    const sectionId = location.hash.slice(1) || routeSection;

    if (sectionId) {
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
      });
    }
  }, [location.hash, location.pathname]);

  return (
    <main className="goldbot-page">
      <section className="goldbot-workspace">
        <header className="goldbot-topbar">
          <div className="goldbot-topbar__brand">
            <span className="goldbot-topbar__mark" aria-hidden="true">
              <svg viewBox="0 0 64 48" role="img">
                <defs>
                  <linearGradient id="goldbar-gradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#fff0a8" />
                    <stop offset="45%" stopColor="#f5bd35" />
                    <stop offset="100%" stopColor="#a96508" />
                  </linearGradient>
                </defs>
                <path d="M15 13h34l10 24H5l10-24Z" fill="url(#goldbar-gradient)" />
                <path d="M15 13 5 37h8l9-18h27l-3-6H15Z" fill="#ffd968" opacity=".75" />
                <path d="M5 37h54" fill="none" stroke="#8b5208" strokeWidth="3" strokeLinecap="round" />
                <text x="32" y="31" fill="#754400" fontSize="11" fontWeight="800" textAnchor="middle">GOLD</text>
              </svg>
            </span>
            <div>
              <span className="goldbot-kicker">Automated gold trading</span>
              <strong>GoldBot Control Center</strong>
            </div>
          </div>
          <GoldBotLiveIndicator />
        </header>

        <nav className="goldbot-section-nav" aria-label="GoldBot page sections">
          {navigation.map((item) => (
            <a className="goldbot-section-nav__link" href={`#${item.id}`} key={item.id}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="goldbot-content">
          <section className="goldbot-scroll-section" id="overview">
            <GoldBotOverview />
          </section>
          <section className="goldbot-scroll-section" id="positions">
            <GoldBotPositions />
          </section>
          <section className="goldbot-scroll-section" id="strategies">
            <GoldBotStrategies />
          </section>
          <section className="goldbot-scroll-section" id="logs">
            <GoldBotLogs />
          </section>
          <section className="goldbot-scroll-section" id="performance">
            <GoldBotPerformance />
          </section>
          <p className="goldbot-disclaimer">Live GoldBot telemetry · Unavailable values are shown as — rather than estimated</p>
        </div>
      </section>
    </main>
  );
}

function GoldBot() {
  return (
    <GoldBotStatusProvider>
      <GoldBotWorkspace />
    </GoldBotStatusProvider>
  );
}

export default GoldBot;
